import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Paper,
  LinearProgress,
  IconButton,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { captureException } from '../utils/captureException';

interface CsvUploadProps {
  onUploadSuccess?: () => void;
  onUploadError?: (error: string) => void;
}

const CsvUpload: React.FC<CsvUploadProps> = ({ onUploadSuccess, onUploadError }) => {
  const { t } = useTranslation();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!file.type.includes('csv') && !file.name.toLowerCase().endsWith('.csv')) {
      return t('settings.csvUpload.invalidFileType');
    }

    // Check file name
    if (file.name !== 'llista.csv') {
      return t('settings.csvUpload.fileNameMustBe');
    }

    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSuccess('');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setError('');
    setSuccess('');
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSuccess('');
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setError('');
    setSuccess('');
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t('settings.csvUpload.fileRequired'));
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setError('');
    setSuccess('');

    try {
      const idToken = await user?.getIdToken();
      if (!idToken) {
        throw new Error('No idToken found');
      }
      const formData = new FormData();
      formData.append('csvFile', selectedFile);

      const url = `${process.env.REACT_APP_API_URL || ''}/api/upload-csv`;
      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 100);

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      setTimeout(() => {
        setSuccess(t('settings.csvUpload.uploadSuccess'));
        setSelectedFile(null);
        setIsUploading(false);
        onUploadSuccess?.();
      }, 500);

    } catch (error) {
      captureException(error, { tags: { feature: 'csvUpload' }, extra: { action: 'uploadCsv' } });
      setIsUploading(false);
      setUploadProgress(0);
      const errorMessage = error instanceof Error ? error.message : t('settings.csvUpload.uploadError');
      setError(errorMessage);
      onUploadError?.(errorMessage);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClickUpload = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      {/* File Selection Area */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          border: '2px dashed',
          borderColor: selectedFile ? 'success.main' : 'primary.main',
          backgroundColor: selectedFile ? 'success.50' : 'background.paper',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.dark',
            backgroundColor: 'primary.50',
          },
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={handleClickUpload}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        <CloudUploadIcon 
          sx={{ 
            fontSize: 48, 
            color: selectedFile ? 'success.main' : 'primary.main',
            mb: 2 
          }} 
        />
        
        <Typography variant="body1" gutterBottom>
          {selectedFile ? selectedFile.name : t('settings.csvUpload.dragDropText')}
        </Typography>
        
        {!selectedFile && (
          <Typography variant="body2" color="text.secondary">
            {t('settings.csvUpload.description')}
          </Typography>
        )}
      </Paper>

      {/* Selected File Info */}
      {selectedFile && (
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="body2" color="success.main">
            {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
          </Typography>
          <IconButton size="small" onClick={handleRemoveFile}>
            <CloseIcon />
          </IconButton>
        </Box>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" gutterBottom>
            {t('settings.csvUpload.uploading')}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2 }}
          action={
            <IconButton size="small" onClick={() => setError('')}>
              <CloseIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Success Alert */}
      {success && (
        <Alert 
          severity="success" 
          sx={{ mt: 2 }}
          action={
            <IconButton size="small" onClick={() => setSuccess('')}>
              <CloseIcon />
            </IconButton>
          }
        >
          {success}
        </Alert>
      )}

      {/* Upload Button */}
      <Button
        variant="contained"
        fullWidth
        onClick={handleUpload}
        disabled={!selectedFile || isUploading}
        sx={{ mt: 2 }}
        startIcon={<CloudUploadIcon />}
      >
        {isUploading ? t('settings.csvUpload.uploading') : t('settings.csvUpload.uploadFile')}
      </Button>
    </Box>
  );
};

export default CsvUpload;
